import { create } from "zustand";
import type { DemoMutation } from "@/lib/demo/demo-mutations";
import type {
    AssistantFeedbackRating,
    AssistantResponseDeliveryMetadata,
} from "@/lib/ai/assistant-v2/response-delivery";
import type { RecordCandidatePreviewPayload } from "@/lib/ai/assistant-v2/record-preview";
export type { RecordCandidatePreviewPayload } from "@/lib/ai/assistant-v2/record-preview";

// ─── Types (exported so assistant-sidebar can import instead of redeclaring) ──

export type CreatedPayload = {
    recordKind: "loreEntry" | "plotThread" | "plotEvent" | "loreConnection";
    recordId: string;
    display: string;
    aiCreatedAt: string;
    demoMutation?: DemoMutation;
};

export type ProposalPayload = {
    proposalId: string;
    kind:
        | "loreEntryEdit"
        | "loreEntryDelete"
        | "lorePageEdit"
        | "lorePageDelete"
        | "plotEventEdit"
        | "plotEventDelete"
        | "plotThreadEdit"
        | "plotThreadDelete"
        | "loreConnectionDelete";
    display: string;
    payload: unknown;
};

export type ToolEvent =
    | { kind: "tool_use_start"; name: string; id: string }
    | {
          kind: "created";
          payload: CreatedPayload;
          state: "live" | "undone" | "expired" | "dismissed" | "error";
          message?: string;
      }
    | {
          kind: "proposal";
          payload: ProposalPayload;
          state: "pending" | "approved" | "rejected" | "error";
          message?: string;
      }
    | { kind: "record_candidate_preview"; payload: RecordCandidatePreviewPayload }
    | { kind: "warning"; message: string }
    | { kind: "error"; message: string };

export interface Msg {
    id: string;
    role: "user" | "assistant";
    text: string;
    events: ToolEvent[];
    streaming: boolean;
    responseId?: string;
    deliveryMetadata?: Readonly<AssistantResponseDeliveryMetadata>;
    feedbackRating?: AssistantFeedbackRating;
    /** True for the synthetic condensed-history message inserted when the chat
     *  exceeds MSG_CAP. Rendered as a subtle divider, not a regular bubble. */
    isSummary?: boolean;
}

export function canApplyAssistantHydration(current: {
    conversationId: string | null;
    messages: Msg[];
    isNewSession?: boolean;
}): boolean {
    return (
        !current.isNewSession && current.conversationId === null && current.messages.length === 0
    );
}

export function buildOrphanProposalMessage(proposal: {
    id: string;
    kind: ProposalPayload["kind"];
    display: string;
    payload: unknown;
    status: string;
    failureMessage?: string | null;
}): Msg {
    return {
        id: `proposal-${proposal.id}`,
        role: "assistant",
        text: "",
        streaming: false,
        events: [
            {
                kind: "proposal",
                payload: {
                    proposalId: proposal.id,
                    kind: proposal.kind,
                    display: proposal.display,
                    payload: proposal.payload,
                },
                state:
                    proposal.status === "applied"
                        ? "approved"
                        : proposal.status === "rejected"
                          ? "rejected"
                          : proposal.status === "failed"
                            ? "error"
                            : "pending",
                message:
                    proposal.status === "applied"
                        ? "Approved"
                        : proposal.status === "rejected"
                          ? "Rejected"
                          : (proposal.failureMessage ?? undefined),
            },
        ],
    };
}

// ─── Store ────────────────────────────────────────────────

interface ChatState {
    messages: Msg[];
    conversationId: string | null;
    input: string;
    streaming: boolean;
    /** Prevents the initial hydration effect from reopening the prior chat after
     * the author intentionally starts a blank session. */
    isNewSession: boolean;
    /** Changes whenever the active session identity changes. Async work captures
     * this value so results from an earlier session cannot mutate the next one. */
    sessionEpoch: number;
    /** Changes whenever the visible message collection changes. */
    messageRevision: number;
}

const DEFAULT_CHAT_STATE: ChatState = {
    messages: [],
    conversationId: null,
    input: "",
    streaming: false,
    isNewSession: false,
    sessionEpoch: 0,
    messageRevision: 0,
};

/** After this many messages the component triggers background summarization. */
export const MSG_CAP = 40;
/** How many leading messages are condensed into the summary. */
export const MSG_SUMMARIZE_COUNT = 20;

function scopeKey(storyId: string, chapterId: string) {
    // Assistant conversations are persisted per story. The chapter ID remains
    // an input to each turn as current context, but does not identify a session.
    void chapterId;
    return storyId;
}

function getOrDefault(conversations: Record<string, ChatState>, key: string): ChatState {
    return conversations[key] ?? DEFAULT_CHAT_STATE;
}

function patch(
    conversations: Record<string, ChatState>,
    key: string,
    update: Partial<ChatState>,
): Record<string, ChatState> {
    return {
        ...conversations,
        [key]: { ...getOrDefault(conversations, key), ...update },
    };
}

interface AssistantChatStore {
    conversations: Record<string, ChatState>;

    getConvo(storyId: string, chapterId: string): ChatState;

    appendMessage(storyId: string, chapterId: string, msg: Msg): void;
    /** Generic updater — use for text streaming and event state flips. */
    updateMessage(
        storyId: string,
        chapterId: string,
        msgId: string,
        updater: (m: Msg) => Msg,
    ): void;

    setConversationId(storyId: string, chapterId: string, id: string): void;
    hydrateConversation(
        storyId: string,
        chapterId: string,
        conversationId: string,
        messages: Msg[],
        options?: { preserveInput?: boolean },
    ): void;
    resetConversation(storyId: string, chapterId: string): void;
    setInput(storyId: string, chapterId: string, input: string): void;
    setStreaming(storyId: string, chapterId: string, streaming: boolean): void;
    /** Replace the first `replaceCount` messages with a single summary bubble. */
    replaceWithSummary(
        storyId: string,
        chapterId: string,
        summaryText: string,
        replaceCount: number,
        expectedMessageRevision?: number,
    ): void;
}

export const useAssistantChatStore = create<AssistantChatStore>((set, get) => ({
    conversations: {},

    getConvo(storyId, chapterId) {
        return getOrDefault(get().conversations, scopeKey(storyId, chapterId));
    },

    appendMessage(storyId, chapterId, msg) {
        const key = scopeKey(storyId, chapterId);
        set((s) => {
            const convo = getOrDefault(s.conversations, key);
            return {
                conversations: patch(s.conversations, key, {
                    messages: [...convo.messages, msg],
                    messageRevision: convo.messageRevision + 1,
                }),
            };
        });
    },

    updateMessage(storyId, chapterId, msgId, updater) {
        const key = scopeKey(storyId, chapterId);
        set((s) => {
            const convo = getOrDefault(s.conversations, key);
            return {
                conversations: patch(s.conversations, key, {
                    messages: convo.messages.map((m) => (m.id === msgId ? updater(m) : m)),
                    messageRevision: convo.messageRevision + 1,
                }),
            };
        });
    },

    setConversationId(storyId, chapterId, id) {
        const key = scopeKey(storyId, chapterId);
        set((s) => ({
            conversations: patch(s.conversations, key, {
                conversationId: id,
                isNewSession: false,
            }),
        }));
    },

    hydrateConversation(storyId, chapterId, conversationId, messages, options) {
        const key = scopeKey(storyId, chapterId);
        set((state) => {
            const current = getOrDefault(state.conversations, key);
            return {
                conversations: patch(state.conversations, key, {
                    conversationId,
                    messages,
                    input: options?.preserveInput ? current.input : "",
                    streaming: false,
                    isNewSession: false,
                    sessionEpoch: current.sessionEpoch + 1,
                    messageRevision: current.messageRevision + 1,
                }),
            };
        });
    },

    resetConversation(storyId, chapterId) {
        const key = scopeKey(storyId, chapterId);
        set((state) => ({
            conversations: patch(state.conversations, key, {
                messages: [],
                conversationId: null,
                input: "",
                streaming: false,
                isNewSession: true,
                sessionEpoch: getOrDefault(state.conversations, key).sessionEpoch + 1,
                messageRevision: getOrDefault(state.conversations, key).messageRevision + 1,
            }),
        }));
    },

    setInput(storyId, chapterId, input) {
        const key = scopeKey(storyId, chapterId);
        set((s) => ({
            conversations: patch(s.conversations, key, { input }),
        }));
    },

    setStreaming(storyId, chapterId, streaming) {
        const key = scopeKey(storyId, chapterId);
        set((s) => ({
            conversations: patch(s.conversations, key, { streaming }),
        }));
    },

    replaceWithSummary(storyId, chapterId, summaryText, replaceCount, expectedMessageRevision) {
        const key = scopeKey(storyId, chapterId);
        set((s) => {
            const convo = getOrDefault(s.conversations, key);
            if (
                expectedMessageRevision !== undefined &&
                convo.messageRevision !== expectedMessageRevision
            ) {
                return s;
            }
            const summaryMsg: Msg = {
                id: `summary-${Date.now()}`,
                role: "assistant",
                text: summaryText,
                events: [],
                streaming: false,
                isSummary: true,
            };
            return {
                conversations: patch(s.conversations, key, {
                    messages: [summaryMsg, ...convo.messages.slice(replaceCount)],
                    messageRevision: convo.messageRevision + 1,
                }),
            };
        });
    },
}));
